// --------------------------------------------------------
// HMV QUESTION - HMVQuestion
// --------------------------------------------------------

// --------------------------------------------------------
// GENERAL
// --------------------------------------------------------
import { List, ListItem, ListItemIcon, Radio, ListItemText, Checkbox, Slider } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react'
import LabelInput from '../../components/labelInput/LabelInput';
import { H2, Text } from '../../components/UI/generalLayout/Layout.styled';
import useResponsiveInfo from '../../hooks/useResponsiveInfo';
import Alternativa from '../../models/core/questionnaire/Alternativa';
import Resposta from '../../models/core/questionnaire/Resposta';
// --------------------------------------------------------

// --------------------------------------------------------
// IMPORTS
// --------------------------------------------------------
// --------------------------------------------------------

// --------------------------------------------------------
// HMV QUESTION INTERFACE
// --------------------------------------------------------

interface IHMVQuestion {
    enunciado: any;
    respostas: any;
    showCheckbox: boolean;
    setAllAnswers: any;
    setTextAnswers: any;
    onChangeCheckOrRadioValue: any;
    onChangeTextValue: any;
    setButtonDisabled: any;
    isViewState: boolean;
}
/**
/**
 * A component that renders a HMV Question.
 *
 * @param props The component props.
 * @returns The component JSX.
 */
const HMVQuestion = ({ isViewState, enunciado, respostas, showCheckbox, setAllAnswers, setTextAnswers, onChangeCheckOrRadioValue, setButtonDisabled }: IHMVQuestion) => {
    // --------------------------------------------------------
    // HMV QUESTION PRIVATE DECLARATIONS
    // --------------------------------------------------------
    const isNotListaOrIntervalo = enunciado.tipoDadoResposta.codigo !== 'lista' && enunciado.tipoDadoResposta.codigo !== 'intervalo';
    const isIntervalo = enunciado.tipoDadoResposta.codigo === 'intervalo';
    const isMultiplaEscolha = enunciado.tipoDadoResposta.codigo === 'lista' && enunciado.maximo === null;
    const numeroPergunta = enunciado.pergunta.numeracao;
    const textoPergunta = enunciado.pergunta.texto;
    const idPergunta = enunciado.pergunta.id;

    const [answer, setAnswer] = useState<any>()
    const [textAnswerError, setTextAnswerError] = useState('')
    const [textAnswer, setTextAnswer] = useState('')
    const [sliderValue, setSliderValue] = useState<number | number[]>(0)
    const [intervalValues] = useState(['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'])
    const [conditionalTextAnswers, setConditTextAnswer] = useState<any[]>([])
    const { isMobile } = useResponsiveInfo()

    const isRadioChecked = (id: string) => {
        return !!answer ? answer.id === id : false;
    }

    const isCheckboxChecked = (id: string) => {
        return !!answer ? answer.filter((a: any) => { return a.id === id }).length > 0 : false
    }

    useEffect(()=>{
        const respostaList = respostas.filter((r: Resposta)=>{ return r.perguntaId === enunciado.pergunta.id})
        if(respostaList.length > 0){
            const resposta = respostaList[0];
            fillAnswer(resposta);
        }
    }, [respostas])

    const setCheckBoxAnswer = (alternativa: any, questionId: string, isTextUpdate: boolean, questionIndex: number) => {
        setAnswer((prev: any) => {
            const list = prev === undefined? [] : [...prev]
            const index = list.findIndex((el: any) => {
                return el.id === alternativa.id
            })

            index === -1 ? list.push(alternativa) : list.splice(index, 1)
            return list;
        });

        setAllAnswers((prev: any) => {
            const list = [...prev]
            const index = prev.findIndex((el: any) => {
                return el.id === alternativa.id
            })
            if (index !== -1) {
                list.splice(index, 1)
                if (isTextUpdate) {
                    list.splice(index, 0, alternativa)
                }
            } else {
                list.push({ questionId: questionId, ...alternativa })
            }

            return [...list];
        })
    }

    const setRadioAnswer = (alternativa: any, questionId: string, ordem: number) => {
        setAnswer(alternativa);
        setAllAnswers((prev: any) => {
            const newList = [...prev]
            const index = newList.findIndex((el: any) => {
                return el.questionId === questionId
            })

            if (index === -1) {
                newList.push({ questionId: questionId, ordemPergunta: ordem, ...alternativa })
            } else {
                newList.splice(index, 1)
                newList.splice(index, 0, { questionId: questionId, ordemPergunta: ordem, ...alternativa })
            }

            return newList
        })
        onChangeCheckOrRadioValue(ordem, questionId, alternativa.id)
    }

    const onChangeAlternativaAssociatedTextAnswer = (alternativa: any, questionId: string, questionIndex: number) => {
        setConditTextAnswer((prev: any) => {
            const list = [...prev];
            const index = list.findIndex(e => {
                return e.id === alternativa.id
            })

            if (index !== -1) {
                list.splice(index, 1)
                list.splice(index, 0, { questionId: questionId, ordemPergunta: questionIndex, ...alternativa })
            } else {
                list.push({ questionId: questionId, ordemPergunta: questionIndex, ...alternativa })
            }
            return list;
        })

        setTextAnswers((prev: any) => {
            const index = prev.findIndex((el: any) => {
                return el.id === alternativa.id
            })

            if (index !== -1) {
                prev.splice(index, 1)
                prev.splice(index, 0, { questionId: questionId, ordemPergunta: questionIndex, ...alternativa })
            } else {
                prev.push({ questionId: questionId, ordemPergunta: questionIndex, ...alternativa })
            }

            return [...prev];
        })
    }

    const onChangeTextAnswer = (text: string, questionId: string, questionIndex: string) => {
        setTextAnswer(text);
        setTextAnswers((prev: any) => {
            const index = prev.findIndex((el: any) => {
                return el.questionId === questionId
            })
            if (text !== '') {
                if (index !== -1) {
                    prev.splice(index, 1)
                    prev.splice(index, 0, { questionId: questionId, ordemPergunta: questionIndex, text: text })
                } else {
                    prev.push({ questionId: questionId, ordemPergunta: questionIndex, text: text })
                }
            } else {
                prev.splice(index, 1)
            }

            return [...prev];
        })
    }

    const onChangeIntervalAnswer = (value: any, questionId: string, isSlider: boolean, questionIndex: string) => {
        setSliderValue(value as number);
        setAnswer({ questionId: enunciado.pergunta.id, ordemPergunta: questionIndex, value: isSlider ? String(value) : value })
        setAllAnswers((prev: any) => {
            const list = [...prev]
            const index = list.findIndex((el: any) => {
                return el.questionId === questionId
            })

            if (index !== -1) {
                list.splice(index, 1)
                list.splice(index, 0, { questionId: questionId, ordemPergunta: questionIndex, value: isSlider ? String(value) : value })
            } else {
                list.push({ questionId: enunciado.pergunta.id, ordemPergunta: questionIndex, value: isSlider ? String(value) : value })
            }

            return list;
        })
    }

    const Interval = ({ value, questionId, questionIndex }: any) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', marginRight: !isMobile ? 0 : 0 }}>
                <div style={{ marginLeft: '40%', marginBottom: -15, justifyContent: "center" }}>
                    <Text style={{ fontSize: 12 }}>{value}</Text>
                </div>
                <Radio
                    disabled={isViewState}
                    checked={!!answer ? answer.value === value : false}
                    onChange={() => { onChangeIntervalAnswer(value, questionId, false, questionIndex); }}
                    value={value}
                    name="radio-buttons"
                />
                <div style={{ textAlign: 'center', width: 80 }}>
                    {value === '1' && (<Text style={{ fontSize: 10 }}>POUCO DESCONFORTO</Text>)}
                    {value === '10' && (<Text style={{ fontSize: 10 }}>MUITO DESCONFORTO</Text>)}
                </div>
            </div>)
    }

    useEffect(() => {
        if (enunciado.tipoDadoResposta.codigo === 'decimal') {
            //verificar se é pergunta sobre o peso
            let regex: RegExp = enunciado.id === "d184cf63-4c7c-425d-a3f8-be19a67ad205" ? RegExp('^([0-9]){1,3}(\,?)([0-9]){1,2}$', 'g') : RegExp('^([0-2]){1}(\,)([0-9]){1,2}$', 'g') ;

            if (textAnswer !== '' && !regex.test(textAnswer)) {
                setTextAnswerError('Formato inválido!')
                setButtonDisabled(true)
            } else {
                setTextAnswerError('')
                setButtonDisabled(false)
            }
        }

    }, [textAnswer, setButtonDisabled])
    
    const fillAnswer = async (resposta: any)=>{
        if (resposta !== null) {
            if (enunciado.tipoDadoResposta.codigo === 'lista') {
                resposta.alternativas.forEach((alt: Alternativa) => {
                    const alternativaInsert = { id: alt.alternativaId, respostaTexto: alt.texto, entradaTexto: alt.texto !== '' }
                    if (isMultiplaEscolha) {
                        setAnswer((prev: any)=>{
                            const lista: any = !!prev? [...prev] : []
                            const index = lista.findIndex((el: any) => {
                                return el.id === alt.alternativaId
                            })

                            index === -1 && lista.push(alternativaInsert) 
                            return lista;
                        })

                        if (alt.texto !== '') {
                            setConditTextAnswer((prev: any)=>{
                                const lista: any = !!prev? [...prev] : []
                                const index = lista.findIndex((el: any) => {
                                    return el.id === alt.alternativaId
                                })

                                index === -1 && lista.push({ questionId: enunciado.pergunta.id, ordemPergunta: enunciado.ordem, ...alternativaInsert, respostaTexto: alt.texto })
                                return lista;
                            })
                        }
                    } else {
                        setRadioAnswer(alternativaInsert, enunciado.pergunta.id, enunciado.ordem)
                    }
                })
            } else if (enunciado.tipoDadoResposta.codigo === 'decimal') {
                onChangeTextAnswer(resposta.valor, enunciado.pergunta.id, enunciado.ordem)
            } else if (enunciado.tipoDadoResposta.codigo === 'intervalo') {
                onChangeIntervalAnswer(resposta.valor, enunciado.pergunta.id, isMobile, enunciado.ordem)
            }
        }
    }

    const getRespostaTextoValue = useCallback((alternativaId: string)=>{
        const lista = conditionalTextAnswers.filter(p=>{ return p.id === alternativaId})
        return lista.length > 0? lista[0].respostaTexto : null;
    }, [conditionalTextAnswers])
    // --------------------------------------------------------
    

    // --------------------------------------------------------
    // REACT RETURN FUNCTION
    // --------------------------------------------------------
    return (
        <>
            <div>
                <H2>{`${numeroPergunta}. ${textoPergunta}`}</H2>
                {isNotListaOrIntervalo
                    && (<LabelInput value={textAnswer}
                        isInputError={isViewState? false : textAnswerError !== ''}
                        inputErrorText={textAnswerError}
                        disabled={isViewState}
                        placeholder="Digite a resposta aqui"
                        onChangeText={(event: any) => { onChangeTextAnswer(event.target.value, idPergunta, enunciado.ordem) }}
                    />)}
                {isIntervalo && (
                    <div style={{ display: 'flex', flexDirection: 'row' }}>
                        {!isMobile ? (<>{intervalValues.map((value: any, index: number) => {
                            return <Interval key={index} value={value} questionId={idPergunta} disabled={isViewState} />
                        })}</>) :
                            <div style={{ marginLeft: 15, marginRight: 15, width: '100%' }}>
                                <Slider
                                    value={sliderValue}
                                    defaultValue={1}
                                    disabled={isViewState}
                                    onChange={(event: Event, newValue: number | number[]) => {
                                        onChangeIntervalAnswer(newValue, idPergunta, true, enunciado.ordem);
                                    }}
                                    valueLabelDisplay="on"
                                    step={1}
                                    marks
                                    min={1}
                                    max={10}
                                />
                            </div>}
                    </div>
                )}
                {enunciado.alternativas.length > 0 && (<>
                    <List>
                        {enunciado.alternativas.map((alternativa: any, index: number) => {
                            return <>
                                <ListItem key={index}>
                                    <ListItemIcon style={{ marginTop: -25 }}>
                                        {showCheckbox ? (
                                            <Checkbox
                                                disabled={isViewState}
                                                edge="start"
                                                checked={isCheckboxChecked(alternativa.id)}
                                                tabIndex={-1}
                                                onClick={() => { setCheckBoxAnswer(alternativa, idPergunta, false, enunciado.ordem) }}
                                                disableRipple
                                                inputProps={{}}
                                            />) : (
                                            <Radio
                                                disabled={isViewState}
                                                checked={isRadioChecked(alternativa.id)}
                                                onChange={() => { setRadioAnswer(alternativa, idPergunta, enunciado.ordem) }}
                                                value="a"
                                                name="radio-buttons"
                                                inputProps={{ 'aria-label': 'A' }}
                                            />)}
                                    </ListItemIcon>
                                    <ListItemText style={{ marginTop: -20 }}>
                                        <Text style={{ fontSize: 14 }}>{alternativa.texto}</Text>
                                    </ListItemText>
                                </ListItem>
                                <div>
                                    {(alternativa.entradaTexto && (isCheckboxChecked(alternativa.id) || isRadioChecked(alternativa.id)))
                                        && (<LabelInput
                                            disabled={isViewState}
                                            style={{ marginBottom: 4 }}
                                            value={getRespostaTextoValue(alternativa.id)}
                                            placeholder="Digite a resposta aqui"
                                            onChangeText={(event: any) => { onChangeAlternativaAssociatedTextAnswer({ ...alternativa, respostaTexto: event.target.value }, idPergunta, enunciado.ordem) }}
                                        />)}
                                </div>

                            </>
                        })}
                    </List>

                </>)}
            </div>
        </>
    )
    // --------------------------------------------------------
}

export default HMVQuestion